// Cross-tab lock for HL Bridge2.deposit. Multiple call sites can trigger
// a deposit independently — the auto-deposit watcher
// (`useHyperliquidAutoDeposit`), the manual deposit modal
// (`HyperliquidDepositModal`), and the stranded-deposit recovery banner
// (`HyperliquidStrandedDepositBanner`). Without coordination, two of
// them firing within seconds of each other submits two sponsored userOps
// for the same Arbitrum balance; the second reverts with "ERC20:
// transfer amount exceeds balance" because the first already drained
// the EOA.
//
// Lock is persisted to localStorage (not just module state) so a user
// with two tabs open of the same app can't independently fire deposits.
// Pattern matches `active-bridge.ts`'s watcher lock: tab-id tag +
// claimedAt timestamp + read-back verification. localStorage has no
// CAS, but the read-after-write check catches the common race where
// two tabs claim within the same animation frame; on a tighter race
// the late-writer wins on read-back and the earlier claimant fails to
// verify ownership.
//
// Holds for at most `LOCK_TTL_MS`. Cleared by `releaseDepositLock` in
// mutation `onSettled`, or expires naturally if a tab dies mid-flight.

const LOCK_KEY = 'hl-deposit-lock'
const LOCK_TTL_MS = 60_000

// Unique per-tab identifier. Module-scoped — survives React StrictMode's
// effect double-invoke (which discards refs between mount/unmount in
// dev), but a hard reload generates a fresh id, which is what we want
// (a new tab session should be able to claim if the previous one died).
const TAB_ID =
	typeof window !== 'undefined'
		? `hl-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
		: 'hl-ssr'

type LockValue = { tabId: string; until: number }

function readLock(): LockValue | null {
	if (typeof window === 'undefined') return null
	try {
		const raw = window.localStorage.getItem(LOCK_KEY)
		if (!raw) return null
		const parsed = JSON.parse(raw) as { tabId?: string; until?: number }
		if (typeof parsed.tabId !== 'string' || typeof parsed.until !== 'number') return null
		return { tabId: parsed.tabId, until: parsed.until }
	} catch {
		// Corrupt JSON — treat as no live lock so a stuck value can't
		// permanently block claims.
		return null
	}
}

function writeLock(value: LockValue): boolean {
	if (typeof window === 'undefined') return false
	try {
		window.localStorage.setItem(LOCK_KEY, JSON.stringify(value))
		return true
	} catch {
		return false
	}
}

export function tryClaimDepositLock(): boolean {
	// SSR has no localStorage, no signer, and no deposit can happen.
	// Return false so any (incorrect) SSR caller bails early instead of
	// thinking it owns an imaginary lock.
	if (typeof window === 'undefined') return false
	const now = Date.now()
	const existing = readLock()
	if (existing && now < existing.until) {
		// Live lock — re-entry from the same tab is allowed (refreshes the
		// claim). A different tab is rejected.
		if (existing.tabId !== TAB_ID) return false
	}
	const want: LockValue = { tabId: TAB_ID, until: now + LOCK_TTL_MS }
	if (!writeLock(want)) return false
	// Read-after-write verify. If another tab raced us within the same
	// animation frame, its write lands after ours and `after.tabId` won't
	// match. localStorage cross-tab writes are serialized by the browser,
	// so whoever wrote last wins — the other tab's claim fails here.
	const after = readLock()
	return after?.tabId === TAB_ID
}

export function releaseDepositLock(): void {
	if (typeof window === 'undefined') return
	const existing = readLock()
	if (!existing) return
	// Only release our own lock — don't clobber another tab's claim.
	if (existing.tabId !== TAB_ID) return
	try {
		window.localStorage.removeItem(LOCK_KEY)
	} catch {
		// Storage may be unavailable (quota, private mode); lock will
		// expire on its own via TTL.
	}
}

export function isDepositLocked(): boolean {
	const existing = readLock()
	if (!existing) return false
	return Date.now() < existing.until
}
